import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import type { RecordRef } from "@/profitability/records/objects";
import { reportsPath } from "@/profitability/report-query";
import type { ReportColumn, ReportRow } from "@/profitability/types";

export interface RecordHistoryPeriod {
  run_id: string;
  start_date: string;
  end_date: string;
  /** Run by the same calculation as the latest, so it compares. */
  comparable: boolean;
  /** The record's row that period; null when it was not in the report. */
  row: ReportRow | null;
}

export interface RecordHistory {
  dataset: {
    id: string;
    name?: string;
    plural?: string;
    columns: ReportColumn[];
  };
  periods: RecordHistoryPeriod[];
}

/**
 * One record across the template's periods, oldest first. The record is
 * named by its id (`record_id`), encoded whole into the path.
 */
export const useRecordHistory = (
  businessId: string,
  reportId: string,
  ref: RecordRef,
  enabled = true,
  throughRunId: string | null = null,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      ...reportKey(businessId),
      "record-history",
      reportId,
      ref.dataset,
      ref.recordId,
      throughRunId,
    ],
    queryFn: ({ signal }) =>
      api
        .get<{
          history: RecordHistory;
        }>(
          `${reportsPath(businessId, reportId)}/records/${encodeURIComponent(ref.dataset)}/${encodeURIComponent(ref.recordId)}/history`,
          { signal, params: { run_id: throughRunId ?? undefined } },
        )
        .then((res) => res.data.history),
    enabled,
    retry: false,
  });
};
